/**
 * The roster: seven people who answer for a department, and the crew.
 *
 * This file is the interface. Nothing in the logic knows a single one of these
 * names — every rule is read off `price`, `window`, `department` and the flags
 * below — so replacing a placeholder with a real person is an edit to this file
 * and nothing else.
 *
 * **The seven gates.** One person per department carries `gate: true`. They are
 * the face of it: first in their department's list, so they are who you are
 * sent to, and the only row that says where that department's opinion of a new
 * hire starts. Everybody else in the department is a colleague — reachable,
 * negotiable, and not the department's answer.
 *
 * Two flags beyond that, and both exist so the file can say something the
 * `price` block cannot:
 *
 *   `starts`  where this department's opinion of a new hire begins. Six of the
 *             seven start at nothing. One does not, and the reason is on her
 *             row.
 *   `gates`   what this person controls if it is not supply. Exactly one of
 *             them gates *assignment* rather than stock, which is why he is
 *             allowed to offer none of the three options without the roster
 *             tests calling it a dead end.
 *   `crew`    a colleague rather than a threshold. See the block below.
 *   `teardown` one of the crew who is sent out when an outdoor function is
 *             struck. They walk the lawn instead of their post while it is
 *             being struck, carrying from the stacks to the carts, and they
 *             are everything they are indoors while they do it: the same eyes,
 *             the same shape, the same opinion of you. See `sim/teardown.js`.
 *
 * ---------------------------------------------------------------------------
 * THE THIRTEEN, WHO ARE NOT GATES AND MUST NOT BECOME THEM
 *
 * Banquets is the department you work in, so it is the one department whose
 * roster is not a set of doors. Thirteen people carry `crew: true` and **none
 * of them has a `price` block at all**, which is the guarantee rather than a
 * flag somebody has to remember to respect: there is no ask, no trade and no
 * take to read, `gatekeepersFor` filters them out, and nothing in
 * `procurement.js` can reach a row that does not exist. They hold no stock and
 * they answer for nothing.
 *
 * What they are is who you work beside, so they differ in **how they behave
 * around you** rather than in what they will hand over. Three fields carry it:
 *
 *   `reacts`  what this person does when they have seen enough. Seven shapes
 *             across thirteen people and never more than two alike — see the
 *             table below, and `react()` in `main.js`, which is the only place
 *             any of them is implemented.
 *   `starts`  where *their own* opinion of a new hire begins. A gate's `starts`
 *             is their department's; a colleague's is theirs personally, and it
 *             is a different ledger. See the block after this one.
 *   `days`    which days of the week they are on at all, Monday first. Most
 *             rows have none and are on every day.
 *   `drifts`  unreliability with a shape to it: on a seeded fraction of days
 *             this person turns up `lateBy` minutes into their own window.
 *             Fixed per day, so it is a person you cannot count on rather than
 *             a coin you can flip until it lands.
 *
 * The seven shapes, and why none of them needed a new system:
 *
 *   files          a write-up, through the same door every other write-up in
 *                  the game goes through. The default, and what staff did
 *                  before any of this existed.
 *   complains      goes and finds a manager instead of filing. Costs you
 *                  Management and leaves no paper — the shape guests already
 *                  had, given to two people who work here.
 *   covers         nothing. Ever. The claim is let go and the record says so.
 *   remembers      nothing, the first few times. It goes on the record as a
 *                  let-go with their name on it, and the count of those *is*
 *                  the memory — no new state anywhere, because the ledger was
 *                  already keeping it.
 *   squares-it     covers, and writes a favour in the ledger. You owe them,
 *                  and it comes back on their schedule like every other debt.
 *   tells-crew     no paper and nothing above the section: Banquets hears, and
 *                  only Banquets.
 *   tells-everyone the Chef's shape, from a witness rather than a theft. Every
 *                  other department hears and his does not, which is worse in
 *                  the way gossip is worse.
 *
 * A crew member is still a person in the building: they have eyes, they walk a
 * route, they keep hours, and every one of those is read by the systems that
 * already existed. The only thing added for them is the dispatch on `reacts`.
 *
 * ---------------------------------------------------------------------------
 * AND WHAT THEY THINK OF YOU, WHICH IS NOT WHAT THEIR DEPARTMENT THINKS
 *
 * Every colleague carries a **personal standing** with the player, on the same
 * ladder as the seven departments and in a completely separate ledger. It moves
 * on what this person witnessed and on what has passed between the two of you —
 * a debt settled, a debt left standing — and **never on a grade**. A room that
 * went well is the department's business. What Ryan thinks of you is Ryan's.
 *
 * It does not change what anybody does. The shape on the row is the shape, at
 * every step of the ladder: the one who files never becomes the one who covers,
 * because a person who would report you is a fact about them and not a mood.
 * What it changes is **how much it takes to get them going** — the bands in
 * `suspicion.js` lift for somebody who thinks well of you and drop for somebody
 * who does not, so the same thing seen by the same person is reported sooner or
 * later or not at all.
 *
 * Two consequences fall out of that and both are on purpose:
 *
 *   - At the top of their ladder a person who reports you **hesitates once**.
 *     Which makes the interesting version of filing reachable: she let one go a
 *     fortnight ago and she is filing this one, and nothing about her changed
 *     except what she thinks of you.
 *   - **Covering costs the person doing it.** Every cover spends their own
 *     standing with you, so the mate who carries you carries you down his own
 *     ladder, and at the bottom of it he stops. He still will not report you —
 *     that is the shape and the shape does not move — he has simply stopped
 *     absorbing it for nothing, and the ledger gets a line for all the times he
 *     did.
 *
 * `starts` is where that number begins. Most of them start at nothing, because
 * most people you have not met have no opinion. Three do not.
 * ---------------------------------------------------------------------------
 *
 * Each entry is four things:
 *
 *   where   a route (usually a post they shuffle around) and, for the ones with
 *           eyes, a vision cone.
 *   when    a window on the clock. Outside it they have gone home, and the
 *           resource they are sitting on is unreachable by any means except
 *           helping yourself to it.
 *   what    `price`: which of ask / trade / take they allow, what each costs,
 *           and what they do about it afterwards.
 *   who     a placeholder line. See the TODO at the bottom.
 *
 * ---------------------------------------------------------------------------
 * A NOTE ON WHAT STANDING IS ALLOWED TO DO TO A PRICE, BECAUSE IT CAME UP
 *
 * Decision 2 settled this: standing buys **doors**, never prices. A department
 * that thinks better of you does not charge less, forgive more, or hand things
 * over cheaper — it opens something that was shut, and leaves a released thing
 * closer to where you were going.
 *
 * Two of the seven have a door rather than a flat refusal, and both say so with
 * a named step of the ladder rather than a number:
 *
 *   `opensAt`      this option is refused below that step and ordinary at or
 *                  above it. Nothing about what it costs changes — the same
 *                  minutes, the same weight, the same debt.
 *   `reportsBelow` asking is itself the offence below that step, and an
 *                  ordinary ask at or above it. One person, on purpose.
 *
 * Every number in every `price` block below is a constant and there is a test
 * that fails if one of them ever becomes a function of how anybody feels.
 * ---------------------------------------------------------------------------
 * AND WHAT THEY MAKE OF YOUR CARD, WHICH IS THE SAME ANSWER TWICE
 *
 * `record: { weighs }` is how heavily this person reads the write-ups against
 * your name. It does not touch the ledger and it does not touch a price. It is
 * subtracted from your standing *as they see it*, so a bad card costs you the
 * same thing every other kind of trouble costs you — a step, and the doors that
 * were on it — and it costs it back the week the card clears.
 *
 * Which means the reactions are already written. Nobody needed a rule about
 * being in trouble: the doors above were always going to be the thing that
 * shut, and all these seven numbers decide is who notices and how much. They
 * are deliberately not the same number.
 *
 *   0     has never once mentioned it                      (hskp-floor)
 *   1     has heard, like everybody has heard              (three of them)
 *   1.2   will not have it near his section                (chef-pass)
 *   1.6   it is a fact about you, and facts get written down (desk-priya)
 *   2     the one who was covering for you                 (sup-banquets)
 *
 * The last one is the highest because he is the only one who had a choice about
 * it. Everybody else is reacting to a stranger's paperwork; he put his name on
 * you.
 * ---------------------------------------------------------------------------
 *
 * The windows are the schedule the whole middle game runs on. On a PM banquet
 * shift (14:00–22:00) you can reach the Shop Foreman only in the first ninety
 * minutes, the Engineer only after that, Housekeeping not at all after six, and
 * Valet not at all before four. Which department you can afford to owe depends
 * on what time it is when you find out you are short.
 */

/**
 * A generous cone: these are people standing at a post, not sentries.
 *
 * Everybody you can take from has one. If their stock is worth stealing they
 * are worth being seen by, and it is the availability window — not luck — that
 * decides whether anyone is looking.
 */
const POST_EYES = { range: 26, halfAngle: 55, dwell: 1.1 };

/**
 * And a shorter one, for people doing a job rather than minding a post.
 *
 * Eighteen feet and a narrower arc. A server carrying four covers is not
 * watching the room, they are watching the tray, and the difference has to be
 * in the cone or thirteen new pairs of eyes turn every room in the building
 * into a surveilled one. It is the whole reason the crew could be added at all
 * without retuning the geography: they see what happens in front of them and
 * they do not see down a corridor.
 */
const WORKING_EYES = { range: 18, halfAngle: 45, dwell: 1.6 };

export const STAFF = [
  // --- management ----------------------------------------------------------
  {
    id: 'mgr-banquets',
    name: 'Curtis Hale',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'sidepart', hairColour: 'grey', face: null, skin: 1, build: { height: 1.04, chest: 1.02, belly: 1.04 }, outfit: { tie: 0x7a2430 } },
    department: 'management',
    /**
     * Management's gate, and the only one of the seven you cannot deal with.
     *
     * He has no `price` worth the name and that is his whole design: there is
     * no ask, no trade and nothing of his to take, so his standing moves only
     * on the things he actually watches — what your rooms graded, whether the
     * shift was clean, and what is on your card. It is the one department you
     * cannot talk your way up.
     *
     * Which makes his warm step the most valuable door in the game, because it
     * is the one that stops the marble being an offence. See `access.js`.
     */
    gate: true,
    starts: 0,
    /**
     * Up and down the service corridor, down the main run and back, with a dip
     * through the service door into the lobby. That dip is the whole point of
     * him: it means the shortcut across the marble is sometimes free and
     * sometimes the worst decision of your shift, and the only way to know
     * which is to have watched him.
     *
     * The leg down the run is the other half of the same idea and it is why the
     * run is the corridor most likely to have somebody in it. There is no door
     * on it to duck through and nothing along it to stand behind, he is the one
     * person on the property who writes you up, and he is always on. A ride
     * that meets him gets the full length of his cone to be met in.
     */
    route: [
      [176, 4, 3],
      [169.5, 4, 0],
      [169.5, 16, 0],
      [150, 42, 6],
      [169.5, 16, 0],
      [169.5, 4, 0],
      [120, 4, 2],
      [70, 4, 4],
      [26, 4, 2],
      [-1, 4, 0],
      [-1, 52, 5],
      [-1, 92, 4],
      [-1, 4, 0],
      [70, 4, 0],
      [120, 4, 0],
    ],
    speed: 1.6,
    /** Always on. A manager with opening hours is not a manager. */
    window: null,
    /** The only one who writes you up for being somewhere you should not be. */
    enforces: true,
    vision: { range: 34, halfAngle: 42, dwell: 1.3 },
    price: {
      ask: { allowed: false, refusal: 'I am not a storeroom. Go and find one.' },
      trade: { allowed: false, refusal: 'I do not do errands with people I roster.' },
      take: { allowed: false, refusal: 'There is nothing of mine to take. That is rather the point.' },
    },
    /*
     * He reads it once, like anybody, and the marble is gated on Management, so
     * one number here quietly does the thing the write-up system was always
     * pretending to do: a card gets the lobby taken off you.
     */
    record: { weighs: 1 },
    /**
     * And the other half of him, which is the half that is new.
     *
     * At the second strike he comes and finds you. Not a notification, not a
     * thing waiting on a noticeboard for whenever you next walk past it — he
     * leaves whatever he was doing, crosses the property, and says it: what it
     * was for, what a third one does, and when the ones you have got clear.
     *
     * `at` is two because the third is the one that costs shifts and a warning
     * delivered after it is not a warning, it is a receipt. `within` is how far
     * off he will break his route for you; outside it he has not found you yet,
     * which is a true thing to be and costs him nothing to be honest about.
     *
     * The words say the number out loud and nothing else. Every date and every
     * count is listed under them by the box that draws this, on purpose: the
     * facts are the warning and they have to survive whoever writes the lines.
     * What he is for is that a player cannot reach the third one and say nobody
     * told them, so the second line is him closing that off in as many words.
     */
    warns: {
      at: 2,
      within: 62,
      lines: [
        'Stop walking. You know what this is.',
        'Two. I am saying it out loud so nobody can tell me later that you did not know.',
        'Three is a fortnight off the roster, and I still have to staff your rooms while you are gone.',
        'So work clean. They come off on their own. I am not doing this twice.',
      ],
    },
    /**
     * Four ways of being spoken to by a man who is always on shift.
     *
     * His is the one department you cannot talk up, so these are the only thing
     * on him that moves at all. Read off Management, which is his department
     * and also his opinion, because with him they are the same thing.
     */
    /**
     * The finale, and the one thing he can do about Grant: stand between him
     * and your card, or hand him the list. `covers` scales what Grant has on
     * you, by the step you are on with Management.
     */
    finale: {
      covers: { trusted: 0.5, warm: 0.75, cold: 1, hostile: 1.5 },
      says: {
        helps: 'He will get my numbers on you, not his. Go and set the room.',
        nothing: 'He will ask me about you. I will tell him what is on your card.',
        hinders: 'He asked for my list. I gave it to him. Dates, times, all of it.',
      },
    },
    greets: {
      hostile: 'Whatever it is, the answer is no.',
      cold: 'Sheet says six. Tell me it is six.',
      warm: 'Room read well last night. Keep it there.',
      trusted: 'You are fine. Go on, I am not checking.',
    },
    charm: {
      cold: 'Long shift. You know how it is.',
      warm: 'You know me. You know it was not like that.',
      ok: 'Fine. Go and do the room.',
      no: 'Save it. I have read your card.',
    },
    line: 'Everything set for six? Because the sheet says six.',
  },

  // --- banquets ------------------------------------------------------------
  {
    /**
     * Banquets' gate, and the only one of the seven who gates nothing you can
     * carry.
     *
     * He is your supervisor. Banquets owns no shortfall — the stock in that
     * storeroom is already yours — so there is nothing to ask him for and
     * nothing of his to take, and `gates: 'assignment'` says so out loud rather
     * than leaving him looking like a dead end to the roster tests. What he
     * controls is which events reach your board at all, which the generator
     * already reads off where his department stands with you.
     *
     * `covers` is the rest of him. He carries you, silently, and he stops
     * silently: past the third thing on your card inside three days he goes
     * down a whole step at once instead of bleeding away a point at a time.
     * There is no warning because a warning is a negotiation, and the entire
     * character is somebody who does not have that conversation with you.
     */
    id: 'sup-banquets',
    name: 'Sergio "Serg" Alvarez',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'slick', hairColour: 'black', face: 'moustache', skin: 4, build: { height: 0.98, chest: 1.1, belly: 1.16, shoulders: 1.04 } },
    department: 'banquets',
    gate: true,
    /*
     * Warm, and the second of the two departments that starts above nothing.
     *
     * The other five have never heard of you. He hired you. Starting your own
     * supervisor at nothing said that the department you work in thinks exactly
     * as little of you as the Kitchen does, which is not a new hire's position —
     * it is a stranger's, and you are on his board for six.
     *
     * It is also what makes `covers` below mean what it says. He carries you
     * and then stops carrying you, and stopping has to cost you something you
     * had: from warm, the step he drops is the one he gave you on your first
     * day. From nothing it went straight to hostile, which is not a supervisor
     * withdrawing a favour, it is one who hates you.
     */
    starts: 30,
    gates: 'assignment',
    /*
     * Two, and it has to be two.
     *
     * The third write-up ends the run, so a supervisor who stops covering at
     * three stops covering at the same moment you are fired — a rule that can
     * only ever fire into a reloaded day, which is not a rule, it is a comment.
     * Two is the last number that means anything: he carries one, and the
     * second one inside three days is the one where he stops.
     */
    covers: { incidents: 2, withinDays: 3 },
    /*
     * Twice, which is the most on the roster, and it is the only one of the
     * seven numbers that is about the person reading rather than the card.
     *
     * Everybody else is reacting to a stranger's paperwork. He is the one who
     * chose to carry you, so he is the one it lands on when carrying you turns
     * out to have been a decision he has to answer for. `covers` above is him
     * stopping; this is him having stopped.
     */
    record: { weighs: 2 },
    /**
     * And the thing he actually holds, which until now was only a comment.
     *
     * `gates: 'assignment'` says he decides which events reach your board, and
     * this is what that is worth the moment `covers` above has run out: he does
     * not take you off the roster, he puts you on the small rooms. Anything
     * over two hours' work goes to somebody he does not have to explain.
     *
     * Hung off the same latch as the covering rather than off a threshold of
     * its own, because they are one decision. He carried you, he stopped, and a
     * supervisor who has stopped carrying you does not hand you the gala. One
     * rule, one moment, two consequences — which is also why it re-opens on its
     * own: the latch re-arms when the incidents age off your card.
     *
     * A door, like everything else standing buys, and deliberately a door with
     * something behind it: the short cards are still there, they still grade,
     * and grading them is still how you climb back. A rule that left an empty
     * board would be a rule you cannot work your way out of, which is the one
     * thing this whole system must never be.
     */
    assigns: { whenStopped: true, keepUnder: 120 },
    /*
     * Banquet storage, at the door end, and *not* the muster alcove — which is
     * where he obviously belongs and where he cannot go.
     *
     * A person standing in the alcove competes with the time clock and the BEO
     * board for the interact key. Objects beat people, so the clock and the
     * board still work, but the step between them is a spot where no object is
     * in reach and the prompt becomes "talk to your supervisor" — and a player
     * crossing that step on the way in gets a conversation instead of a punch.
     * It cost the benchmark two minutes and a grade before it was noticed.
     */
    route: [
      [106, -8, 7],
      [106, -16, 5],
    ],
    /*
     * A new starter's first afternoon, and only that.
     *
     * He meets them at the clock, which is the one day he is anywhere near it:
     * in the corridor at the mouth of the alcove, facing the clock, not in the
     * alcove — the rule above is about the step between the clock and the
     * board, and this is fifteen feet from it, twice the reach of a
     * conversation. He stays until the first room is accepted and then walks
     * back to storage by its own door. A reset that is not a new starter never
     * sees this, which is why the benchmark cannot.
     */
    firstDay: {
      post: [99, 5],
      faces: [99, -10],
      back: [[134, 5], [134, -4]],
      says: 'You are the new one. One room on the board, the small one. Roz is short on linen for it. Go and ask her.',
    },
    speed: 1.5,
    window: { from: '13:30', to: '22:30' },
    vision: POST_EYES,
    price: {
      ask: { allowed: false, refusal: 'I hand out rooms. Stock is your problem.' },
      trade: { allowed: false, refusal: 'You do not do errands for me. You work for me.' },
      take: { allowed: false, refusal: 'It is your own storeroom. Nothing to take.' },
    },
    greets: {
      hostile: 'I moved you. Do not ask me why.',
      cold: 'Board is up. You are on six.',
      warm: 'Six is yours and I am not going to check it.',
      trusted: 'Took the gala off Anthony and gave it to you. Do not make me look stupid.',
    },
    /**
     * The one sentence he says on the way down.
     *
     * Not a warning and not an explanation, because a warning is the start of a
     * negotiation and the whole character is somebody who will not have that
     * conversation with you. It is an instruction, it names what `assigns`
     * above already does to your board, and it closes.
     */
    stops: 'You are on the small rooms now. Do not ask me how long.',
    charm: {
      cold: 'Give me one room to put it right.',
      warm: 'You know I am good for it.',
      ok: 'One. Then I am done being reasonable.',
      no: 'I have heard it. Go and work.',
    },
    line: 'You are on the board for six. I put you there. Do not make me move you.',
  },
  /*
   * --- the crew ------------------------------------------------------------
   *
   * Thirteen of them, and the reason they are thirteen rather than three is
   * that Banquets is the one department the player is *in*. Everywhere else on
   * this roster is a threshold with a person in front of it; this is the
   * section, and a section is people you work a shift beside.
   *
   * Read the `reacts` line on each row and nothing else about them matters to
   * the logic. They are ordered by where they are rather than by rank, because
   * that is how you meet them.
   */

  // --- the ballroom --------------------------------------------------------
  {
    /**
     * The room, the branch, and the back of the room by way of the run.
     *
     * Anthony is the reason the service entrance at the bottom of the run is a
     * way into the ballroom rather than a hole in a wall: somebody uses it, on a
     * lap, and the lap crosses the whole length of the run to get there.
     *
     * The overtime king. He picks up every callout on the schedule, which is why
     * his window is the longest on the crew, and he is on the floor for all of
     * it, so he is one of the two who file. Nothing personal in it: he is on the
     * clock for everything that happens in that room, so everything that
     * happens in that room goes on a form.
     */
    id: 'capt-banquets',
    /** The daily job they ask for (`data/jobs.js`). */
    jobs: ['flip'],
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "Not now. I have been here since Tuesday.",
      cold: "Sheet. Room. Go. I have a callout to cover.",
      warm: "You are on the Ashford. I am on everything else.",
      trusted: "There you are. Take the Ashford, I will take the other four.",
    },
    name: 'Anthony "Ant Man"',
    department: 'banquets',
    crew: true,
    reacts: 'files',
    // Out on the lawn when one is struck: it is his crew and his lawn.
    teardown: true,
    route: [
      [81, 20, 5],
      [60, 30, 4],
      [81, 20, 0],
      [81, 4, 3],
      [120, 4, 3],
      [81, 4, 0],
      [-1, 4, 0],
      [-1, 92, 0],
      [16, 92, 4],
      [-1, 92, 0],
      [-1, 4, 0],
      [81, 4, 0],
    ],
    speed: 1.8,
    window: { from: '13:00', to: '23:00' },
    /*
     * The working cone and not the post one, and that is a geography decision
     * rather than a character one. His lap is the only crew route that touches
     * the main run, which is the most exposed leg in the game; a twenty-six
     * foot cone on it would have made the corridor run a different proposition
     * without anybody deciding to change it. Eighteen feet, moving at one and
     * four fifths metres a second, is a person who would notice you if you were
     * in front of him and does not sweep the hall.
     */
    vision: WORKING_EYES,
    says: {
      caught: 'I saw that. I am on the clock for all of it, so all of it goes down.',
      accused: 'Me? I cover your callouts. I am writing this one down.',
    },
    charm: {
      cold: 'Anthony. One minute.',
      warm: 'Ant Man. You know me.',
      ok: 'One minute. I do not have more than that.',
      no: 'No. I have three rooms and a callout. Go and set yours.',
    },
    line: 'Somebody called out again. I am on till close. I am always on till close.',
  },
  {
    /**
     * The first named person on the roster, and the mate of the first week: he
     * covers, and he never brings it up again.
     *
     * He works the room, which is why his route stays inside the ballroom and
     * well clear of the main run. Putting another pair of eyes on the run would
     * have quietly changed the most exposed leg in the game, and a crew member
     * added for the fiction is not allowed to retune the geography.
     */
    id: 'lil-d',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "Mm.",
      cold: "Alright.",
      warm: "Ay. You good?",
      trusted: "Look who it is. Sit down, you look rough.",
    },
    name: 'Lil D',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'hightop', hairColour: 'black', face: null, skin: 7, build: { height: 0.94, chest: 0.94, shoulders: 0.94 } },
    department: 'banquets',
    crew: true,
    reacts: 'covers',
    /*
     * The only one who starts liking you, and it is the same fact as his
     * reaction rather than a second one: he is the mate, he was going to cover
     * for you on day one, and the ladder has to start somewhere that makes that
     * true before you have done anything to earn it.
     *
     * It also means he is the one you can most obviously *spend*. A cover costs
     * him, he starts with the most to lose, and a player who leans on him all
     * week will watch a warm number come down.
     */
    starts: 35,
    /*
     * And the one who sees Ryan do it, on that first shift. He covers, so he
     * looks the other way, and a new starter watching learns two things at
     * once: people notice, and you can see what they are going to do about it
     * before they do it.
     */
    firstDay: { sees: 'srv-ryan' },
    route: [
      [42, 58, 5],
      [62, 58, 4],
      [62, 38, 5],
      [42, 38, 4],
    ],
    speed: 1.5,
    /*
     * Five hours in the middle of the banquet shift, and that is the price of
     * him: somebody who covers for you and never mentions it has to be paid for
     * in availability instead, or there is a corner of the shift where nothing
     * you do in front of anybody costs anything.
     */
    window: { from: '15:30', to: '20:30' },
    vision: POST_EYES,
    says: {
      caught: 'Did not see it. Do not tell me about it.',
      accused: 'Wow. Okay. I will just stand over here, then.',
      /* The one he writes, once, on the way to the bottom of his own ladder. */
      favor: 'I have carried enough of them. This one is yours.',
    },
    charm: {
      cold: 'D. Come on.',
      warm: 'D. My guy.',
      ok: 'Yeah, yeah. Go.',
      no: 'Nah. Not this one.',
    },
    line: 'First week is all of it at once. Second week it is only most of it.',
  },
  {
    /**
     * Billy, "Broward County Bill": the shortcut, and a can of something in his
     * hand while he finds it.
     *
     * He tells the section everything and Management nothing, and the
     * distinction is the whole of him. He is not covering for you: by the end of
     * service every person in banquet blacks has heard about it and the
     * department is a little colder for it. He would be offended to be called a
     * snitch, because nobody above the section ever hears a word.
     */
    id: 'srv-billy',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "Not now, I am on my second can.",
      cold: "Bill. Yeah.",
      warm: "Heard about table nine? Quicker way to do it, too.",
      trusted: "Watch this. Half the time, same room.",
    },
    name: 'Billy "Broward County Bill"',
    department: 'banquets',
    crew: true,
    reacts: 'tells-crew',
    route: [
      [78, 62, 3],
      [78, 86, 2],
      [58, 86, 3],
      [78, 86, 0],
    ],
    speed: 1.6,
    window: { from: '16:00', to: '23:30' },
    vision: WORKING_EYES,
    says: {
      caught: 'Oh, that is going round the section. Sorry. It is.',
      accused: 'Me? Bro. The whole section is hearing about this by ten.',
    },
    charm: {
      cold: 'Bill. Keep it quiet.',
      warm: 'Broward. Keep it in the section.',
      ok: 'Fine. Only the section.',
      no: 'Too late. I have told three people between cans.',
    },
    line: 'There is always a quicker way. I have usually found it, and been told off for it.',
  },
  {
    /**
     * Ryan, "Rye Guy", who is always hungry: he saw, he said nothing, and he
     * will be collecting.
     *
     * The only reaction on the roster that writes into the ledger rather than
     * onto your card, which makes him the most interesting of the seven shapes
     * and the easiest to misread: at the moment it happens he is
     * indistinguishable from somebody who covered for you. The difference turns
     * up two days later in the middle of a setup, on his schedule, exactly like
     * every other debt in this game.
     */
    id: 'srv-ryan',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "Busy. Hungry.",
      cold: "Hey. Is there food?",
      warm: "You want a hand with those? Is that a sandwich?",
      trusted: "Give me two of those. And half of whatever that is.",
    },
    name: 'Ryan "Rye Guy"',
    department: 'banquets',
    crew: true,
    reacts: 'squares-it',
    // One of the three who strike a lawn. See `teardown` above.
    teardown: true,
    /*
     * On a new starter's first shift, the one thing that happens in front of
     * them that is not theirs: a pastry off the room's tray, for later, because
     * he is always hungry. He squares things; somebody else will see it, and
     * what they do about it is the lesson.
     */
    firstDay: { does: { kind: 'pastry', says: 'One for later.' } },
    route: [
      [20, 30, 4],
      [20, 54, 3],
      [34, 54, 4],
      [20, 54, 0],
    ],
    speed: 1.5,
    window: { from: '15:00', to: '22:00' },
    vision: WORKING_EYES,
    says: {
      caught: 'I did not see anything. You know what that costs. Lunch, probably.',
      accused: 'Me? After all those pastries? You owe me for that one.',
      favor: 'You remember what I did not see. Come on. I am starving.',
    },
    charm: {
      cold: 'Ryan. Nothing happened.',
      warm: 'Rye Guy. You are a legend.',
      ok: 'Nothing happened. Obviously. Is that a pastry?',
      no: 'Something happened. I am tired of it, and I am hungry.',
    },
    line: 'Is anyone eating that? I am not going to eat it. I am going to look after it.',
  },
  {
    /**
     * Christian, "Nighthawk", on the bar: he never misses anything, and he is
     * the second of the two who file.
     *
     * Not the Captain's reason. Anthony files because the room is his and he is
     * on the clock for it; Christian files because he saw it, and he sees
     * everything, and a thing he saw that he did not write down would bother
     * him more than you would. Same outcome, different reason. Aaron is his
     * brother.
     */
    id: 'bar-christian',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "I saw that. Whatever it was.",
      cold: "Bar is closed to staff.",
      warm: "Water? You have been on your feet since three.",
      trusted: "One water, on the house. I see everything, and I saw nothing.",
    },
    name: 'Christian "Nighthawk"',
    department: 'banquets',
    crew: true,
    reacts: 'files',
    /*
     * Starts against you, and it is nothing personal: a new starter is somebody
     * he has not watched yet, and he watches. Which makes him the fastest of the
     * thirteen to say something on day one and the most dramatic to turn round:
     * the same ladder that has him reporting a glimpse this week has him
     * letting the same glimpse go past once he knows what you are like.
     */
    starts: -25,
    // The lobby end of the room, on the bar's own side of the pillars.
    route: [
      [88, 16, 8],
      [88, 24, 6],
    ],
    speed: 1.1,
    window: { from: '17:00', to: '23:59' },
    vision: POST_EYES,
    says: {
      caught: 'I did not miss it. I never miss it. It is going in.',
      accused: 'I see everything. I would have seen that. It is going in.',
    },
    charm: {
      cold: 'Christian. You did not see that.',
      warm: 'Nighthawk. Come on. Let it go past.',
      ok: 'Fine. I saw it and I am letting it go past. Once.',
      no: 'I see everything. I am writing everything.',
    },
    line: 'Nothing gets past this bar. Ask my brother. He has tried.',
  },
  {
    /**
     * Noemi, "Mimi", on AV, who is in four days out of seven and knows everyone
     * in the hotel.
     *
     * `days` is the awkwardness and the reaction is the reason it matters: she
     * is the one person on the crew with the Chef's shape. She wires every room
     * in the building and she knows every person in it, the valets and the
     * night auditor and the chef, and what she does with a thing she saw is
     * mention it to all six other departments and never to her own. Nothing
     * lands on your card and the whole property is a little cooler with you by
     * Sunday.
     */
    id: 'av-mimi',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "Mind the cable. I know who you are.",
      cold: "Mind the cable.",
      warm: 'Mind the cable. Have you met Priya yet? You should.',
      trusted: "Take this end. And the night auditor says hi.",
    },
    name: 'Noemi "Mimi"',
    department: 'banquets',
    crew: true,
    reacts: 'tells-everyone',
    /** Thursday to Sunday. Monday is day one, so the week counts from zero. */
    days: [3, 4, 5, 6],
    route: [
      [50, 92, 9],
      [62, 92, 7],
    ],
    speed: 1,
    window: { from: '12:00', to: '20:00' },
    vision: WORKING_EYES,
    says: {
      caught: 'Interesting. Very interesting. Who else knows.',
      accused: 'Me? Everybody in this hotel is going to hear you said that.',
    },
    charm: {
      cold: 'Mimi. Do not.',
      warm: 'Mimi. Between us.',
      ok: 'Between us. Obviously. Mind the cable.',
      no: 'I know everyone in this building. They are all going to know.',
    },
    line: 'I know everybody in this hotel. The valets, the chef, the night auditor. Everybody.',
  },
  {
    /**
     * Aaron, "Busta": part-time, Friday to Monday, and he lifts the room. The
     * second of the two who go and find a manager.
     *
     * Christian's brother, and nothing like him: Christian watches, Aaron lifts.
     * He has not worked out that there is a difference between a thing that
     * needs saying and a thing that needs saying *to Curtis*, and there is
     * nothing calculating in it, which is exactly why it costs you: a complaint
     * arrives second-hand, you are not in the room for it, and the first you
     * know is your standing.
     */
    id: 'srv-aaron',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "Oh. Hi.",
      cold: "Grab the other end.",
      warm: "Grab the other end. I have got this side.",
      trusted: "Stand back. I will lift the whole room.",
    },
    name: 'Aaron "Busta"',
    department: 'banquets',
    crew: true,
    reacts: 'complains',
    /*
     * Four days a week and delighted to be here. He likes everybody, which is
     * not the same as covering for them: he will still go and find a manager,
     * he will just need to be a good deal more certain first.
     */
    starts: 20,
    /** Friday, Saturday, Sunday and Monday: the week counts from Monday at zero. */
    days: [4, 5, 6, 0],
    route: [
      [26, 70, 3],
      [40, 70, 3],
      [26, 70, 0],
    ],
    speed: 1.7,
    window: { from: '16:00', to: '23:00' },
    vision: WORKING_EYES,
    says: {
      caught: 'Sorry, man. I think I have to say something.',
      accused: 'Me? No. I am going to find a manager. Right now.',
    },
    charm: {
      cold: 'Aaron. Please do not.',
      warm: 'Busta. It is fine. Honestly.',
      ok: 'Okay. Okay. Nothing happened.',
      no: 'Sorry, man. I am going to go and find someone.',
    },
    line: 'Friday to Monday. I lift the room and I go home. My brother does the watching.',
  },
  {
    /**
     * Alex, night strike: smoke breaks, and a car that will not start. The
     * second of the two who tell the section.
     *
     * Not Billy's reason. Billy tells people things because he enjoys it; Alex
     * does it because he strips the room at eleven at night with three other
     * people and between cigarettes there is nothing else to talk about. Same
     * six points off Banquets and nothing anywhere else.
     *
     * His window is the only one on the crew that wraps past midnight, which is
     * most of what he is: on a PM banquet shift you meet him in the last hour,
     * or out in the staff lot after it, or you never meet him.
     */
    id: 'clean-alex',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "Do not. I am on a break.",
      cold: "Evening. Got a light?",
      warm: "Evening. Leave the linens, I will get them after this one.",
      trusted: "Go home. I will strike it. If my car starts, I am going home too.",
    },
    name: 'Alex',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'long', hairColour: 'darkbrown', face: 'stubble', skin: 2, build: { height: 1.01, chest: 0.96 } },
    department: 'banquets',
    crew: true,
    reacts: 'tells-crew',
    route: [
      [20, 16, 4],
      [20, 30, 4],
      [32, 20, 3],
    ],
    speed: 1.4,
    // From nine, so he is on the floor for the last hour of a PM shift and
    // still about, out in the lot, when it lets out at ten.
    window: { from: '21:00', to: '03:00' },
    vision: WORKING_EYES,
    says: {
      caught: 'The night lot will hear. Nobody above us will.',
      accused: 'The guy whose car does not start. Sure. The section will love this.',
    },
    charm: {
      cold: 'Alex. Not tonight.',
      warm: 'Alex. Let me have this one.',
      ok: 'Go on. I am going out for one anyway.',
      no: 'I strike rooms. I do not strike this.',
    },
    line: 'Car would not start again. Got a lift in. Five minutes, I am going out for one.',
  },

  // --- banquet storage and the back -----------------------------------------
  {
    /**
     * Mornings, and the first of the two who say nothing and keep count.
     *
     * The most quietly unpleasant shape on the roster, because for two
     * incidents she is indistinguishable from somebody covering for you — the
     * record says the same thing his does, it came to nothing — and on the
     * third one the whole lot arrives at once. There is no warning because
     * there is no conversation; that is the point of her.
     *
     * The count is not stored anywhere. Every time she lets one go the ledger
     * writes a row with her name on it, and the number of those rows *is* what
     * she remembers.
     */
    id: 'lead-banquets',
    /** The daily job they ask for (`data/jobs.js`). */
    jobs: ['refresh'],
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "The sheet said eight.",
      cold: "Par is par.",
      warm: "Shelf is right. Check it anyway.",
      trusted: "I put two aside for you. Do not tell the Captain.",
    },
    name: 'Hollis Trask',
    department: 'banquets',
    crew: true,
    reacts: 'remembers',
    /** The third one. Two is a bad week and she has had bad weeks. */
    after: 2,
    route: [
      [112, -6, 7],
      [112, -14, 4],
    ],
    speed: 1.3,
    window: { from: '05:00', to: '14:30' },
    vision: POST_EYES,
    says: {
      caught: 'I know what was on that shelf. I am not saying anything yet.',
      accused: 'I sent you looking for him and you come back with me. I will remember this.',
    },
    charm: {
      cold: 'Hollis. The count is fine.',
      warm: 'Hollis. I will put it right.',
      ok: 'Put it right and we are level.',
      no: 'The shelf had eight. I am keeping count.',
    },
    line: 'Par is par. If the sheet says eight, the shelf had eight this morning.',
  },
  {
    /**
     * Setup crew, mornings, and the second of the two who simply cover.
     *
     * Same outcome as Lil D and a completely different man behind it: Lil D
     * likes you, and this one has been doing this for nineteen years and has
     * never once seen a write-up improve a room. He would cover for anybody. He
     * has covered for people he cannot stand.
     */
    id: 'house-ray',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "Hm.",
      cold: "Morning.",
      warm: "Morning. Room goes up at six.",
      trusted: "Morning. Kettle is on, and I did your chairs.",
    },
    name: 'Ray Okonkwo',
    department: 'banquets',
    crew: true,
    reacts: 'covers',
    /*
     * Nineteen years of it, and he would cover for anybody — but he does not
     * know you, and a man who would cover for anybody is not a man who thinks
     * anything of you in particular. He starts at nothing and he has the
     * shallowest well of the two coverers because of it.
     */
    starts: 0,
    // The lane between two rows of shelving, which is where a room gets built.
    route: [
      [110, -32, 5],
      [138, -32, 4],
    ],
    speed: 1.4,
    window: { from: '06:00', to: '14:00' },
    vision: WORKING_EYES,
    says: {
      caught: 'I did not see it and I will not be asked.',
      accused: 'Fine. I did not hear that, and I am not going to hear it twice.',
      favor: 'I have looked the other way all week. Not this morning.',
    },
    charm: {
      cold: 'Ray. Morning.',
      warm: 'Ray. You are the best of them.',
      ok: 'Morning. Go on.',
      no: 'Not this morning.',
    },
    line: 'Room goes up, room comes down. Nothing that happens in between is news.',
  },
  {
    /**
     * China and glass, at the far end of storage, and the second of the two who
     * keep count.
     *
     * Hollis counts because she is a lead and it is her shelf. This one counts
     * because she is twenty-three and has learned that saying something the
     * first time gets you nothing and a reputation. She holds it longer than
     * Hollis does, and when it goes it goes the same way.
     */
    id: 'stew-imani',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "I am counting.",
      cold: "In and out, that is the job.",
      warm: "Count is good. Yours was fine.",
      trusted: "Yours is always fine. I stopped checking.",
    },
    name: 'Imani Blake',
    department: 'banquets',
    crew: true,
    reacts: 'remembers',
    // Counts the chairs back onto the carts when a lawn is struck.
    teardown: true,
    /** One more than Hollis, because she has learned to wait. */
    after: 3,
    // The deep end, past everything anybody else has a reason to walk to.
    route: [
      [150, -52, 8],
      [166, -52, 6],
    ],
    speed: 1.2,
    window: { from: '15:00', to: '21:30' },
    vision: POST_EYES,
    says: {
      caught: 'That is one off my count. I will hold it for now.',
      accused: 'Noted. I keep a list, and you are on it now.',
    },
    charm: {
      cold: 'Imani. It will come back.',
      warm: 'Imani. It always comes back.',
      ok: 'It had better. Go.',
      no: 'It is three now. I am not holding three.',
    },
    line: 'I count it in and I count it out. That is the entire job and people find it funny.',
  },
  {
    /**
     * Max, "Milly Vanilly", in the break room and on his phone, and the one you
     * cannot count on.
     *
     * `drifts` is him: something under half the time he is two hours into his
     * own window before he appears, and which days those are is fixed by the
     * day rather than rolled when you look. So he is a person whose shift you
     * learn not to plan around, which is a different kind of awkward from a
     * short window: Hollis is reliably gone by half two and he is simply not
     * there yet, again, and when he is there he is on a call.
     *
     * And he squares it, which fits: a man who owes the section two hours is a
     * man who understands an arrangement. He is also who hands a new starter
     * the pen, in the break room, on the day their first room is graded.
     */
    id: 'house-max',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: "One sec. On a call.",
      cold: "Yeah, one sec.",
      warm: "One sec. Okay. Coffee?",
      trusted: "Hang on, I will call you back. What do you need?",
    },
    name: 'Max "Milly Vanilly"',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'curly', hairColour: 'blond', face: null, skin: 0, build: { height: 1.0, chest: 0.93, belly: 0.95, shoulders: 0.95 }, outfit: { sleeveEnd: 'rolled' } },
    department: 'banquets',
    crew: true,
    reacts: 'squares-it',
    /*
     * The break room, which is the honest place to put him. Getting from here
     * to banquet storage means the service corridor, and the corridor is the
     * one stretch of property a crew route is not allowed to touch: thirteen
     * new pairs of eyes on the most exposed leg in the game would have retuned
     * the geography by accident.
     */
    route: [
      [76, -8, 4],
      [84, -8, 3],
      [84, -26, 5],
      [76, -26, 4],
    ],
    speed: 1.5,
    window: { from: '14:00', to: '22:00' },
    /** Something under half of days, two hours late, decided by the day. */
    drifts: { odds: 0.45, lateBy: 120 },
    vision: WORKING_EYES,
    says: {
      caught: 'Did not see it. I was on the phone. You are welcome.',
      accused: 'Me? I gave you the pen. We are not square any more.',
      favor: 'I covered for you. You are covering for me. Now.',
    },
    charm: {
      cold: 'Max. Put the phone down.',
      warm: 'Milly Vanilly. We are both terrible.',
      ok: 'We are both terrible. Fine. One sec.',
      no: 'I was on the phone. I saw it anyway. No.',
    },
    line: 'Sorry, I have to take this. I always have to take this.',
  },
  {
    /**
     * Juanito Reyes, mornings, who runs the air walls.
     *
     * Six until four, every day, and gone by the time a PM shift is into its
     * stride. Ordinary and likeable, and nothing about him is special in the
     * rules: his shape is the one a person who has a view about doing things
     * properly has, which is going to find a manager rather than filing, and
     * he covers for you exactly as much as anybody with that shape does, which
     * is not at all.
     *
     * What he has that nobody else on the crew does is a job: he is the one
     * man on the AM crew who runs the folding walls out on their tracks and
     * splits the ballroom. His post is inside the ballroom's far service door,
     * because the walls are his, and it is where Roz, at her post by that door,
     * catches sight of him; the corridor is the one stretch a crew route may
     * not touch, so his lap never leaves the room. He is not
     * often at the post. His lap goes into the east air wall pocket and he is
     * in there a long while. What he is doing in there is his business;
     * `sought` is the errand that goes looking for him (`data/searches.js`),
     * and `says` is what he has to say when you find him.
     */
    id: 'house-juanito',
    /** Four steps of the ladder, four ways of looking up. */
    greets: {
      hostile: 'I am on the walls.',
      cold: 'Walls are fine.',
      warm: 'Morning. Walls are fine, before you ask.',
      trusted: 'Morning. You want the room in three again?',
    },
    name: 'Juanito Reyes',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'crop', hairColour: 'black', face: 'moustache', skin: 4, build: { height: 1.03, chest: 0.95, belly: 0.96 }, pieces: ['cap'], outfit: { cap: 0x1f2a3c, sleeveEnd: 'rolled' } },
    department: 'banquets',
    crew: true,
    reacts: 'complains',
    sought: 'find-juanito',
    /*
     * Who he talks to at four. Read by `sim/story.js` and by nothing that
     * draws anything: nothing on the screen, in the record or in his lines
     * says so until the pages turn up at the end.
     */
    feeds: 'ownership',
    // Dwells are seconds, which on the clock is minutes: a quarter of an hour
    // at his post, and two hours in the pocket.
    route: [
      [33, 16, 15],
      [97, 58, 0],
      // Round the end of the pocket door, and in.
      [103, 58, 0],
      [103.2, 53.5, 120],
      [103, 58, 0],
      [97, 58, 0],
    ],
    speed: 1.1,
    window: { from: '06:00', to: '16:00' },
    vision: WORKING_EYES,
    says: {
      caught: 'Sorry. I have to tell somebody about that.',
      // Into the phone, as you come round the pocket door, and then it is away.
      phone: 'Yeah. Yeah, I have to go. Later.',
      found: 'Sorry. The track sticks on this end, I was freeing it off. Hollis wants the walls? I am on it.',
      owes: 'We are good. We are good, right? Anything you need.',
    },
    charm: {
      cold: 'Juanito. Come on.',
      warm: 'Juanito. Between us.',
      ok: 'Okay. Between us.',
      no: 'Sorry. I have to say something.',
    },
    line: 'The walls are on a track. The track is older than me.',
  },

  // --- kitchen -------------------------------------------------------------
  {
    /**
     * Kitchen's gate. He does not do favours and he is not punishing you for
     * asking — the refusal is a fact about him, not a penalty, and it is the
     * one door on this roster that opens at the top of the ladder rather than
     * in the middle. Until then there is exactly one way to get anything out of
     * the Kitchen, and it costs half an hour.
     *
     * The take is the interesting half. He does not ring Management, because
     * ringing Management is an admission that he cannot handle his own kitchen.
     * He tells *everyone else* instead, which is worse in the way gossip is
     * worse: one small hit to all six of the other departments, arriving for
     * nothing you did to any of them.
     */
    id: 'chef-pass',
    name: 'Chef Danny Obando',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'bald', hairColour: 'black', face: 'goatee', skin: 6, build: { height: 1.0, chest: 1.12, belly: 1.1, shoulders: 1.06 } },
    department: 'kitchen',
    gate: true,
    starts: 0,
    // The lane north of the pass, where the chef's own route runs. Two people
    // on the same line rather than a second post, because that is where the
    // kitchen works from.
    route: [
      [-13, -6, 7],
      [-23, -6, 4],
      [-13, -6, 0],
    ],
    speed: 1.8,
    window: { from: '14:00', to: '22:30' },
    vision: POST_EYES,
    price: {
      ask: {
        allowed: true,
        opensAt: 'trusted',
        refusal: 'I do not do favours. I do work.',
        callsIn: true,
        coolDays: 2,
        weight: 1,
      },
      /*
       * The honest option, and the one he takes away.
       *
       * It opens at the bottom step, which every new hire is already standing
       * on — so in ordinary weather this changes nothing and is not meant to.
       * What it means is that a card is enough to drop you underneath it, and
       * a kitchen that will not send you on an errand is a kitchen with
       * nothing left to offer you but helping yourself. Which he will then
       * tell everybody about.
       */
      trade: { allowed: true, opensAt: 'cold', minutes: 30, refusal: 'Not with your name on it. No.' },
      take: { allowed: true, tells: 6, noticed: false },
    },
    /** Gossip is his whole temperament, so of course he has heard. */
    record: { weighs: 1.2 },
    finale: {
      shortfall: 'chairs-short',
      says: {
        helps: 'Chairs are staged. Tell the man with the tablet the kitchen did that.',
        nothing: 'My chairs are at my pass. You know where my pass is.',
        hinders: 'Staff meal. Every chair I have. Go and tell your man that.',
      },
    },
    greets: {
      hostile: 'Off my pass.',
      cold: 'Everything on here is on a ticket. Including that.',
      warm: 'Wait until service is down and then ask me properly.',
      trusted: 'Two minutes. Then it is yours and we never spoke.',
    },
    /**
     * What he says at each of the four places the price block lets you reach.
     *
     * `caught` is the loud one on purpose: his take is `tells: 6`, so helping
     * yourself off his pass is the one theft in the building that goes round
     * every department except his own.
     */
    says: {
      ask: 'Take it. We never had this conversation.',
      trade: 'You want it, you walk for it. Thirty minutes. Go.',
      favor: 'Now. You remember what I gave you.',
      caught: 'That was mine. Everyone in this building is going to know it was you.',
    },
    charm: {
      cold: 'Chef. Thirty seconds. Please.',
      warm: 'Chef, come on. It is me.',
      ok: 'Thirty seconds. That was twenty-nine.',
      no: 'No. Out of my kitchen.',
    },
    line: 'Everything on that pass is spoken for. Including the thing you are looking at.',
  },
  {
    id: 'chef-kitchen',
    /** The daily job they ask for (`data/jobs.js`). */
    jobs: ['coffee'],
    name: 'Chef de Cuisine',
    department: 'kitchen',
    // Beside the pass, in the open, where somebody can actually talk to him.
    route: [
      [-20, -6, 7],
      [-30, -6, 4],
      [-20, -6, 0],
    ],
    speed: 1.9,
    window: { from: '15:00', to: '21:00' },
    /**
     * The one who only trades.
     *
     * He will not do you a favour and he will not let you help yourself. What
     * he will do is give you work, and then give you the thing. There is no way
     * to get anything off the Kitchen cheaply and that is the character.
     */
    price: {
      ask: { allowed: false, refusal: 'There are no favours in this kitchen. There is a service.' },
      trade: { allowed: true, minutes: 30 },
      take: { allowed: false, refusal: 'It is behind me and I am looking at you.' },
    },
    line: 'Your counts are wrong. They are always wrong. Forty-eight, was it.',
  },
  {
    id: 'steward-night',
    name: 'Night Steward',
    department: 'kitchen',
    route: [
      [-40, -12, 6],
      [-40, -20, 5],
    ],
    speed: 1.4,
    window: { from: '20:30', to: '05:00' },
    vision: POST_EYES,
    price: {
      ask: { allowed: true, callsIn: true, coolDays: 1, weight: 1 },
      trade: { allowed: false },
      take: { allowed: true, floorTo: -55, noticed: true, noticeCost: 8 },
    },
    line: 'Chef is gone. Whatever you take, I did not see it and I will not say so.',
  },

  // --- housekeeping --------------------------------------------------------
  {
    /**
     * Housekeeping's gate, and the reason this department starts where it does.
     *
     * Twenty-two years on the property. She has watched a hundred new hires
     * through their first week and she helps before anybody asks — which is
     * what `starts` is for: Housekeeping is the one department outside your own
     * that already thinks well enough of you to leave the linen half way to the
     * room, on day one, having never met you. It is the tutorial the game does
     * not have to write, because the geography teaches it: the shortest fetch
     * in the property is short because of her.
     *
     * And the most expensive thing on the roster to lose. She will not report
     * you — she does not need to — but taking off her cart costs you the whole
     * step in one go, and hers is the slowest to climb back: she asks for
     * almost nothing, so there is almost nothing to settle, and settling is
     * most of how anybody climbs.
     */
    id: 'hskp-floor',
    name: 'Rosalind "Roz" Vetter',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'bun', hairColour: 'auburn', face: null, skin: 0, glasses: true, build: { height: 0.96, chest: 0.94, hips: 1.05 } },
    department: 'housekeeping',
    gate: true,
    /*
     * Warm, and the only row on the roster that is not nothing. Written as the
     * number rather than as the name of the step because this file does not
     * import the ladder — there is a test that reads it back as a step, so it
     * cannot drift away from one quietly.
     */
    starts: 30,
    /*
     * Four feet and a long dwell. She is a floor lead with twenty-two years on
     * the property; she does not pace, she stands at her cart and watches the
     * corridor, and everybody comes to her.
     */
    /*
     * West of the parked carts, on the corridor's walking line, in the open.
     *
     * Her own carts are at x=40..50 and standing among them is the obvious
     * place for her — and it is a spot where anything thrown at her hits a cart
     * instead, which is a fact about the corridor rather than about her.
     */
    route: [
      [32, 4, 9],
      [28, 4, 6],
    ],
    speed: 1.1,
    window: { from: '09:00', to: '19:00' },
    vision: POST_EYES,
    price: {
      // Four days, and light. She will let it go far longer than anybody else
      // and she asks for less when she does, which is the same fact read twice.
      ask: { allowed: true, callsIn: true, coolDays: 4, weight: 0.6 },
      trade: { allowed: true, minutes: 12 },
      take: { allowed: true, dropsRung: true, noticed: false },
    },
    /**
     * Zero, and it is the most deliberate number in this file.
     *
     * She is the floor you land on. Twenty-two years on the property and a
     * completely settled opinion about paperwork, which is that it is not hers
     * and it is not about her linen. Everything else in the building gets
     * colder when you are in trouble; she is exactly as helpful on your worst
     * week as on your first, and the whole shape of the escalation depends on
     * her being that — a player two strikes down has to have somewhere that
     * still works, or the system is a spiral rather than a squeeze.
     *
     * There is a test that fails if this is ever anything but nothing.
     */
    record: { weighs: 0 },
    /**
     * The finale, and what she does about it. `sim/story.js` reads the rung and
     * does the rest through the ordinary release: see `data/story.js`.
     */
    finale: {
      shortfall: 'linen-short',
      says: {
        helps: 'Linen is in the room. Every cloth, pressed, and nobody had to ask me.',
        nothing: 'Linen is where it always is. Come and ask like anybody.',
        hinders: 'I counted my linen twice this morning. All of it is spoken for.',
      },
    },
    greets: {
      hostile: 'Not today, love.',
      cold: 'Cart is where it always is. Do not make a mess of it.',
      warm: "There is a stack by the ballroom door with nobody's name on it.",
      trusted: 'I put it aside before the count. You were never here.',
    },
    /**
     * The only one of the seven who refuses nothing, so there is no refusal
     * here and there is not supposed to be one. `caught` is the exception she
     * does have: her take carries `dropsRung`, and this is the sound of it.
     */
    says: {
      ask: 'Go on then. I never counted it.',
      trade: 'Do that for me first and it is yours. Twelve minutes.',
      favor: 'You owe me and I am calling it. Now, please.',
      caught: 'That was mine. I will remember that it was mine.',
    },
    charm: {
      cold: 'Sorry. Sorry. Long one.',
      warm: 'Roz. You are the only one who talks to me.',
      ok: 'All right, all right. Off you go.',
      no: 'No. I like you and the answer is still no.',
    },
    line: 'Left you two on the cart by the ballroom door. Do not make a thing of it.',
  },
  {
    id: 'hskp-lead',
    name: 'Housekeeping Lead',
    department: 'housekeeping',
    route: [
      [46, 3, 6],
      [58, 3, 4],
      [46, 3, 0],
    ],
    speed: 1.4,
    window: { from: '08:00', to: '16:00' },
    vision: POST_EYES,
    price: {
      ask: { allowed: true, callsIn: true, coolDays: 1, weight: 1.4 },
      trade: { allowed: true, minutes: 22 },
      take: { allowed: true, floorTo: -60, noticed: true, noticeCost: 12 },
    },
    line: 'Those linens came off my shelf. I know because I counted them.',
  },
  {
    id: 'linen-room',
    name: 'Linen Room Attendant',
    department: 'housekeeping',
    route: [
      [52, 3, 8],
      [40, 3, 4],
    ],
    speed: 1.1,
    window: { from: '13:00', to: '18:00' },
    vision: POST_EYES,
    /**
     * The one who always covers and never calls it in.
     *
     * `callsIn: false` is the whole character: she will give you anything and
     * the favour never comes back. She is also only on the floor for five hours
     * a day, which is the only thing stopping her from trivialising the entire
     * system — knowing she exists changes how you plan a shift rather than
     * removing the need to.
     */
    price: {
      ask: { allowed: true, callsIn: false, coolDays: 0, weight: 0 },
      trade: { allowed: true, minutes: 12 },
      take: { allowed: true, floorTo: -40, noticed: false },
    },
    line: 'Take it. Write it on the sheet or do not, I am not the police.',
  },

  // --- valet ---------------------------------------------------------------
  {
    /**
     * Valet's gate, and the only yes on the roster that is not a promise.
     *
     * He says yes to everything, instantly, at every step of the ladder, and
     * about half of it arrives. That is not him lying — it is him having said
     * yes to four other people between you asking and him getting to it. A
     * failed one costs nothing at all: no debt, no standing, nothing but the
     * walk you made to ask.
     *
     * Which is the whole trade. He is the cheapest option in the building and
     * the only one you cannot plan around, so asking him is what you do when
     * you have the minutes to lose and not the minutes to spend. The roll is
     * seeded on the day and the shortfall, so asking twice gets the same
     * answer: he is unreliable, not a slot machine.
     *
     * And he owes everybody, so he collects constantly — the shortest cooling
     * off on the roster, which means his debts come back while you can still
     * remember incurring them.
     */
    id: 'valet-tino',
    name: 'Tino Marchetti',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'quiff', hairColour: 'black', face: 'stubble', skin: 3, build: { height: 1.02, shoulders: 1.08, chest: 1.02 }, without: ['valetcap'] },
    department: 'valet',
    gate: true,
    starts: 0,
    /*
     * At the doors, looking out.
     *
     * He used to pace twenty feet inside the dock, sideways to the doors, and
     * from there the apron was twenty-eight feet away — two past what he can
     * see — so the one opening between the building and the desert had nobody
     * on it. A valet watches the way the cars come in. He stands at the edge
     * of the dock floor now, facing the first roll-up door, and steps back
     * off it for a moment each lap. Still on the dock floor, so still a walk
     * from the corridor for anybody who wants to ask him for something, and
     * nowhere near the run.
     */
    route: [
      [24, -36, 3],
      [24, -40.5, 12],
    ],
    speed: 2,
    window: { from: '12:00', to: '23:00' },
    vision: POST_EYES,
    price: {
      ask: {
        allowed: true,
        opensAt: 'cold',
        refusal: 'Ah, not this week. Ask me next week. Sorry, man.',
        callsIn: true,
        coolDays: 1,
        weight: 1.1,
        deliversOdds: 0.5,
      },
      trade: { allowed: true, minutes: 16 },
      take: { allowed: true, noticed: false },
    },
    /*
     * Ordinary weight, and the softest refusal on the roster. He owes
     * everybody, so he cannot afford to be seen doing favours for somebody
     * with a card, and he is far too pleasant to say that out loud.
     */
    record: { weighs: 1 },
    /*
     * He says yes to the finale like he says yes to everything, and this time
     * it arrives. Hostile, he chains the near doors for a delivery.
     */
    finale: {
      shortfall: 'rounds-short',
      blocks: 'dock-blocked',
      says: {
        helps: 'Rounds are off the truck and in. All of them. I checked, I actually checked.',
        nothing: 'Rounds are on the dock. I will get to it. I will. Probably.',
        hinders: 'Sorry, mate. Delivery tonight. The near doors are chained.',
      },
    },
    greets: {
      hostile: 'Mate, I am slammed. Three cars deep.',
      cold: 'Yeah. Yeah, no, what do you need.',
      warm: 'For you, yeah. Remind me later though. Seriously, remind me.',
      trusted: 'Done. It is done. Well. It is as good as done.',
    },
    /**
     * `ask` is deliberately the most confident line on the roster, because his
     * `deliversOdds` is a half. He is not lying to you. He means it when he
     * says it and then the drive fills up.
     */
    says: {
      ask: 'Yeah, course. Leave it with me.',
      trade: 'Do us one first and then we are square.',
      favor: 'Hey. Hey. That thing. It is now, mate.',
      caught: 'Was that ours? I thought that was ours.',
    },
    charm: {
      cold: 'Two seconds. Two seconds, that is all.',
      warm: 'Tino. Tino. You love me.',
      ok: 'Yeah, whatever. Go on.',
      no: 'Not today. I am three cars deep and one man short.',
    },
    line: 'Yes. Yeah. No, absolutely, I will sort it. What was it again?',
  },
  {
    id: 'valet-capt',
    name: 'Valet Captain',
    department: 'valet',
    /*
     * Outside, at the apron gate.
     *
     * When the drive is three cars deep the overflow goes round the back into
     * the staff lot, and the only way a car gets from the dock road into the
     * lot is the apron gate — the wide one, the one the file says vans come
     * through. So that is where the captain stands in the evening: on the
     * apron, facing through the gate into the lot, and turning back to look
     * down the apron at the far end of each lap. The first person on the
     * roster whose post is out of doors.
     */
    route: [
      [14, -63, 4],
      [4, -63, 14],
    ],
    speed: 1.7,
    window: { from: '16:00', to: '23:59' },
    vision: POST_EYES,
    price: {
      ask: { allowed: true, callsIn: true, coolDays: 1, weight: 1.2 },
      trade: { allowed: true, minutes: 24 },
      take: { allowed: true, floorTo: -60, noticed: true, noticeCost: 10 },
    },
    line: 'Your wedding party is three cars deep on the drive. Not my problem yet.',
  },
  {
    id: 'dock-runner',
    name: 'Dock Runner',
    department: 'valet',
    route: [
      [30, -14, 5],
      [14, -14, 5],
    ],
    speed: 2,
    window: { from: '05:30', to: '14:00' },
    vision: POST_EYES,
    price: {
      ask: { allowed: true, callsIn: true, coolDays: 1, weight: 0.8 },
      trade: { allowed: true, minutes: 18 },
      take: { allowed: true, floorTo: -45, noticed: true, noticeCost: 6 },
    },
    line: 'Anything on the dock is on the dock. I only move what is on the sheet.',
  },

  // --- engineering ---------------------------------------------------------
  {
    /**
     * Engineering's gate. Slow, exact, and the only person here whose estimate
     * is the truth.
     *
     * Everybody else's errand quotes you a number and then gives you twice it
     * to get there, which is slack the player never sees and quietly relies on.
     * His does not: `exact` means the deadline is the number he said. Forty
     * minutes is the longest trade on the roster and it is forty minutes, not
     * eighty, so it is the one errand you can actually be late for.
     *
     * His middle step is worth more than anybody else's and it was not designed
     * that way — it fell out of the map. The shop door is three feet wide and a
     * loaded cart has never gone through it, so a cold riser fetch is two
     * hundred feet on foot, in your arms, twice. Warm moves the stack to a
     * dolly in the branch corridor, which is not half the walk: it is the
     * difference between an arms-only fetch and a cart run.
     */
    id: 'eng-wendell',
    name: 'Wendell Boyce',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'buzz', hairColour: 'grey', face: 'beard', skin: 2, build: { height: 1.03, chest: 1.14, belly: 1.18, shoulders: 1.06 } },
    department: 'engineering',
    gate: true,
    starts: 0,
    // The far end of the shop, which is where he would rather be: past the
    // bench, up the clear lane, with the door a long way behind him.
    route: [
      [-36, 25, 9],
      [-36, 35, 6],
    ],
    speed: 1,
    window: { from: '10:00', to: '20:00' },
    vision: POST_EYES,
    price: {
      ask: {
        allowed: true,
        opensAt: 'cold',
        refusal: 'Not off the books. Not for you, not this week.',
        callsIn: true,
        coolDays: 3,
        weight: 1,
      },
      trade: { allowed: true, minutes: 40, exact: true },
      take: { allowed: false, refusal: 'It is signed out or it is not. Which is it.' },
    },
    /*
     * Ordinary weight, and the one whose trade stays open whatever your card
     * says — because the trade is the paperwork. He will not do you a favour
     * while you are in trouble; he will absolutely still do the job properly
     * by seven, and he is the only gate of whom that stays true.
     */
    record: { weighs: 1 },
    /*
     * Risers, and the way itself. Warm or better, every chain and padlock on
     * the job is off, whoever put it there. Hostile, the cart is locked.
     */
    finale: {
      shortfall: 'risers-missing',
      blocks: 'lift-locked',
      clears: true,
      says: {
        helps: 'Risers are set, and every chain on your way in is off. Properly.',
        nothing: 'Risers are in the shop. There is a work order. Sign it.',
        hinders: 'Cart is locked and the risers are behind a work order. Both correct.',
      },
    },
    greets: {
      hostile: 'There is no version of this where I say yes.',
      cold: 'Is there a work order? Then there is nothing to discuss.',
      warm: 'I can do it right or I can do it now. Pick one.',
      trusted: 'Forty minutes. Do not stand there watching me do it.',
    },
    /**
     * No `caught`, and that is his row rather than an omission: his take is
     * refused outright, so there is no moment in the game where he catches you
     * at it. The cage is locked and he is standing in front of it.
     */
    says: {
      ask: 'Fine. It is off the books and you owe me a clean week.',
      trade: 'Forty minutes. Not thirty-five. Go.',
      favor: 'The clean week. I am collecting it now.',
    },
    charm: {
      cold: 'I know. I know. Two minutes of your time.',
      warm: 'Wendell. You know I do it properly.',
      ok: 'Two minutes. I am timing it.',
      no: 'You are talking and the job is not getting done.',
    },
    line: 'I can do it properly by seven. I cannot do it by six. Those are the two options.',
  },
  {
    id: 'shop-foreman',
    name: 'Shop Foreman',
    department: 'engineering',
    route: [
      [-40, 26, 7],
      [-52, 26, 4],
    ],
    speed: 1.2,
    window: { from: '07:00', to: '15:30' },
    vision: POST_EYES,
    price: {
      ask: { allowed: true, callsIn: true, coolDays: 2, weight: 1.3 },
      trade: { allowed: true, minutes: 26 },
      take: { allowed: false, refusal: 'The cage is locked and I am standing in front of it.' },
    },
    line: 'Everything in here has a work order on it. Yours does not.',
  },
  {
    id: 'engineer-oncall',
    name: 'Engineer on Call',
    department: 'engineering',
    route: [
      [-24, 24, 6],
      [-16, 22, 4],
      [-24, 24, 0],
    ],
    speed: 1.2,
    window: { from: '15:30', to: '23:30' },
    vision: POST_EYES,
    price: {
      ask: { allowed: false, refusal: 'Put it in writing. Then I will still say no.' },
      trade: { allowed: true, minutes: 28 },
      take: { allowed: true, floorTo: -65, noticed: true, noticeCost: 14 },
    },
    line: 'I can get you into that room. I am not saying I will.',
  },

  // --- front desk ----------------------------------------------------------
  {
    /**
     * Front Desk's gate, and the only person in the building where *asking* can
     * cost you something.
     *
     * She is not corrupt and she is not cruel. She is procedural: a member of
     * banquet staff standing on her marble asking her to hand over event
     * signage off the board is an incident, and an incident gets written down.
     * So below the middle step the ask is the offence — it goes on your card
     * and it costs you with Management, who are the people she reports to and
     * the people whose opinion the marble is gated on.
     *
     * At the middle step it is simply an ask, because by then you are somebody
     * she knows rather than somebody on her marble. That is the sharpest door
     * on the roster: the same key press is a write-up on Monday and a favour on
     * Friday, and nothing in the menu tells you which. Finding out is the
     * lesson, and the trade is always there and always fair for anybody who
     * would rather not find out.
     */
    id: 'desk-priya',
    name: 'Priya Raghunathan',
    /** How they look, the same every time: `world/appearance.js`. */
    look: { hair: 'ponytail', hairColour: 'black', face: null, skin: 4, build: { height: 0.97, chest: 0.95, hips: 1.02 }, outfit: { tie: 0xe7c7ef } },
    department: 'frontdesk',
    gate: true,
    starts: 0,
    route: [
      [170, 22, 8],
      [162, 22, 5],
      [170, 22, 0],
    ],
    speed: 1.2,
    window: { from: '06:30', to: '18:30' },
    vision: POST_EYES,
    price: {
      ask: {
        allowed: true,
        reportsBelow: 'warm',
        reportsTo: 'management',
        callsIn: true,
        coolDays: 2,
        weight: 1,
      },
      trade: { allowed: true, minutes: 18 },
      take: { allowed: false, refusal: 'Off the board. In front of guests. No.' },
    },
    /**
     * The second heaviest, and the sharpest in practice, because of what it
     * lands on.
     *
     * Her ask is not a door that shuts — it is a door that turns into an
     * offence. Everybody else in trouble gets a refusal; asking her is the
     * incident. So a card does not cost you her help, it costs you the warm
     * step you spent a fortnight earning to stop asking her being reportable,
     * and the same key press that was a favour on Friday is a fourth write-up
     * on Monday with nothing on screen to say so.
     *
     * She is not being cruel. She has read the file, the file is a fact, and
     * facts are what she does with her day.
     */
    record: { weighs: 1.6 },
    finale: {
      shortfall: 'signage-missing',
      says: {
        helps: 'Your signage is up at the entrance. I set the letters myself.',
        nothing: 'The board is at my desk. On the marble. As always.',
        hinders: 'The board stays at my desk. It is on a form now.',
      },
    },
    greets: {
      hostile: 'I have written down that you were here.',
      cold: 'If it is wrong, it goes on a form.',
      warm: 'Tell me what you need before a guest walks up.',
      trusted: 'I will fix the number and the form can say what it likes.',
    },
    /**
     * She has no `caught` for the same reason he has none: her take is refused
     * on the marble in front of guests. What she has instead is
     * `reportsBelow`, and the only way to find out is to ask her once.
     */
    says: {
      ask: 'I will make the number work. Do not ask me twice.',
      trade: 'Run that for me and I will sort your board.',
      favor: 'Now, and before the shift change. Please.',
    },
    charm: {
      cold: 'This is not what it looks like.',
      warm: 'Priya. You know I would not.',
      ok: 'I know. Go, before somebody sees us talking.',
      no: 'It is exactly what it looks like. I am going to write it down.',
    },
    line: 'That board is signed off. If it is wrong, it gets a form, not a favour.',
  },
  {
    id: 'desk-agent',
    name: 'Front Desk Agent',
    department: 'frontdesk',
    // Just inside the lobby service door, which is front of house, which means
    // the four second uniform clock is running the whole time you are talking.
    route: [
      [166, 16, 7],
      [158, 22, 5],
      [166, 16, 0],
    ],
    speed: 1.3,
    window: { from: '07:00', to: '17:00' },
    vision: POST_EYES,
    /**
     * The one who reports you for asking.
     *
     * `reportsYou` means the ask does not resolve anything — it produces a
     * write-up and a standing hit and leaves the shortfall exactly where it
     * was. There is no tell on the menu beyond her department and her
     * reputation, and finding out is the lesson.
     */
    price: {
      // No `callsIn`: the ask never gets far enough to owe anybody anything.
      ask: { allowed: true, reportsYou: true },
      trade: { allowed: false },
      take: { allowed: true, floorTo: -70, noticed: true, noticeCost: 16 },
    },
    line: 'You are on the wrong side of a wall and we both know it.',
  },
  {
    id: 'night-auditor',
    name: 'Night Auditor',
    department: 'frontdesk',
    route: [
      [172, 22, 8],
      [164, 30, 4],
    ],
    speed: 1,
    window: { from: '23:00', to: '07:00' },
    vision: POST_EYES,
    price: {
      ask: { allowed: true, callsIn: true, coolDays: 2, weight: 1 },
      trade: { allowed: true, minutes: 20 },
      take: { allowed: true, floorTo: -50, noticed: false },
    },
    line: 'Nobody is awake. Nobody is watching. Ask me for anything.',
  },
];

export const STAFF_BY_ID = Object.fromEntries(STAFF.map((person) => [person.id, person]));

/**
 * The crew's old ids, to the real people now in those slots. Read only by a
 * save written before the real crew, so what each slot thought of the player
 * carries over to whoever is in it now.
 */
export const RENAMED_CREW = Object.freeze({
  'srv-yusuf': 'srv-billy',
  'srv-bea': 'srv-ryan',
  'bar-nadia': 'bar-christian',
  'av-tomasz': 'av-mimi',
  'srv-sunny': 'srv-aaron',
  'clean-errol': 'clean-alex',
  'house-greer': 'house-max',
  'port-cal': 'house-juanito',
});

/**
 * Everyone who can be dealt with on behalf of a department.
 *
 * Two exclusions and they are different in kind. The manager is left out
 * because he holds nothing — there is no ask, trade or take on his row and
 * never will be. The crew are left out because they are **not a supply
 * question at all**: they are the section you work in, they answer for nothing,
 * and a negotiation with a colleague about stock that is already yours is a
 * category error.
 *
 * Belt and braces, and deliberately so. No crew row has a `price` block, so
 * there is nothing for `optionsFor` to read even if one of them reached it —
 * but the filter is here as well, because "nothing in procurement knows their
 * names" is a promise worth keeping in two places rather than one.
 */
export function gatekeepersFor(department) {
  return STAFF.filter(
    (person) => person.department === department && !person.enforces && !person.crew && person.price
  );
}

/** The section you work in: colleagues, not thresholds. */
export const CREW = Object.freeze(STAFF.filter((person) => person.crew));

/**
 * The one person who answers for a department.
 *
 * Not the only one you can deal with — every colleague in the department still
 * has a price and a window — but the one whose row carries the department's
 * disposition: where its opinion of a new hire starts, and whatever else is
 * true of it that no individual negotiation expresses.
 */
export function gateFor(department) {
  return STAFF.find((person) => person.gate && person.department === department) ?? null;
}

export const GATES = Object.freeze(STAFF.filter((person) => person.gate));

/**
 * Where every department's opinion of a new hire begins.
 *
 * Read off the gates rather than written as a table of its own, because the
 * disposition belongs to the person who embodies it: one department starts
 * somewhere other than nothing, and the row that says why is the row that says
 * how much.
 */
export const STARTING_STANDING = Object.freeze(
  Object.fromEntries(GATES.map((person) => [person.department, person.starts ?? 0]))
);

export default STAFF;

/*
 * ---------------------------------------------------------------------------
 * TODO — THE REAL ROSTER
 *
 * The thirteen placeholders are slots, not characters. Names, lines and
 * departments stand in; the *shapes* are the design. When the rest of the real
 * people arrive, three of these shapes have to survive somewhere in the roster
 * or the system loses its corners:
 *
 *   - one who always covers and never calls it in   (linen-room)
 *   - one who only trades                           (chef-kitchen)
 *   - one who reports you for merely asking         (desk-agent)
 *
 * What a named character will want on top of what is here:
 *   - `dialogue`: a table keyed by standing tier, replacing `line`.
 *   - `schedule`: window and route per shift, so the same person is on the dock
 *     at six and in the break room at two. Today's pair becomes the default.
 *   - `relationships`: who they will and will not be seen with, so the clique
 *     war reads from the corridor and not only from the standing panel.
 *   - `grudges`: a memory of what you took, so a second theft costs more than
 *     the first. `take.noticed` is the hook that is already there.
 * ---------------------------------------------------------------------------
 */
